/**
 * Notification Utilities
 * Centralized functions for managing notification dismiss states
 * 
 * Usage for new notifications:
 * 1. Define a unique NOTIFICATION_TYPE string (e.g., "missing_reports", "backup_reminder")
 * 2. Use these functions with your notification type
 */

// Storage key prefixes
const ENABLED_PREFIX = "notification_enabled_";
const SESSION_DISMISS_PREFIX = "notification_session_";
const PERMANENT_DISMISS_PREFIX = "notification_permanent_";
const ITEM_DISMISS_PREFIX = "notification_item_";

/**
 * Get today's date string for storage keys (YYYY-MM-DD format)
 */
export const getTodayString = () => {
  return new Date().toISOString().split("T")[0];
};

// ============================================
// Global Enable/Disable (persists in localStorage)
// ============================================

/**
 * Check if a notification type is globally enabled
 * @param {string} notificationType - Unique identifier for the notification
 * @returns {boolean}
 */
export const isNotificationEnabled = (notificationType) => {
  const stored = localStorage.getItem(`${ENABLED_PREFIX}${notificationType}`);
  return stored === null ? true : stored === "true"; // Default to enabled
};

/**
 * Set whether a notification type is globally enabled
 * @param {string} notificationType - Unique identifier for the notification
 * @param {boolean} enabled - Whether to enable or disable
 */
export const setNotificationEnabled = (notificationType, enabled) => {
  localStorage.setItem(`${ENABLED_PREFIX}${notificationType}`, enabled.toString());
};

// ============================================
// Session Dismiss (clears on browser close/new login)
// ============================================

/**
 * Check if notification was dismissed for this session
 * @param {string} notificationType - Unique identifier for the notification
 * @returns {boolean}
 */
export const isSessionDismissed = (notificationType) => {
  return sessionStorage.getItem(`${SESSION_DISMISS_PREFIX}${notificationType}`) === "true";
};

/**
 * Dismiss notification for this session (until next login)
 * @param {string} notificationType - Unique identifier for the notification
 */
export const dismissForSession = (notificationType) => {
  sessionStorage.setItem(`${SESSION_DISMISS_PREFIX}${notificationType}`, "true");
};

// ============================================
// Permanent Dismiss for Date (for notifications that are date-specific)
// ============================================

/**
 * Check if notification was permanently dismissed for a specific date
 * @param {string} notificationType - Unique identifier for the notification
 * @param {string} date - Date string (YYYY-MM-DD), defaults to today
 * @returns {boolean}
 */
export const isPermanentlyDismissedForDate = (notificationType, date = getTodayString()) => {
  const key = `${PERMANENT_DISMISS_PREFIX}${notificationType}_${date}`;
  return localStorage.getItem(key) === "true";
};

/**
 * Permanently dismiss notification for a specific date
 * @param {string} notificationType - Unique identifier for the notification
 * @param {string} date - Date string (YYYY-MM-DD), defaults to today
 */
export const dismissPermanentlyForDate = (notificationType, date = getTodayString()) => {
  const key = `${PERMANENT_DISMISS_PREFIX}${notificationType}_${date}`;
  localStorage.setItem(key, "true");
};

// ============================================
// Item-Specific Dismiss (for notifications with multiple items like jobs)
// ============================================

/**
 * Check if a specific item was dismissed for a specific date
 * @param {string} notificationType - Unique identifier for the notification
 * @param {string} itemId - Unique identifier for the item (e.g., job ID)
 * @param {string} date - Date string (YYYY-MM-DD), defaults to today
 * @returns {boolean}
 */
export const isItemDismissedForDate = (notificationType, itemId, date = getTodayString()) => {
  const key = `${ITEM_DISMISS_PREFIX}${notificationType}_${itemId}_${date}`;
  return localStorage.getItem(key) === "true";
};

/**
 * Permanently dismiss a specific item for a specific date
 * @param {string} notificationType - Unique identifier for the notification
 * @param {string} itemId - Unique identifier for the item (e.g., job ID)
 * @param {string} date - Date string (YYYY-MM-DD), defaults to today
 */
export const dismissItemForDate = (notificationType, itemId, date = getTodayString()) => {
  const key = `${ITEM_DISMISS_PREFIX}${notificationType}_${itemId}_${date}`;
  localStorage.setItem(key, "true");
};

/**
 * Filter out items that have been dismissed for today
 * @param {string} notificationType - Unique identifier for the notification
 * @param {Array} items - Array of items with an 'id' property
 * @param {string} idKey - Key to use for item ID (default: 'id')
 * @returns {Array} Items that haven't been dismissed
 */
export const filterDismissedItems = (notificationType, items, idKey = "id") => {
  const today = getTodayString();
  return items.filter(item => !isItemDismissedForDate(notificationType, item[idKey], today));
};

// ============================================
// Notification Types Registry
// ============================================

export const NOTIFICATION_TYPES = {
  MISSING_REPORTS: "missing_reports",
  BACKUP_REMINDER: "backup_reminder",
  // Add new notification types here as needed
  // Example: LOW_INVENTORY: "low_inventory",
  // Example: CONTRACT_EXPIRING: "contract_expiring",
};
